import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  Eye,
  Brain,
  Zap,
  Database,
  Lightbulb,
  ChevronDown,
  ArrowRight,
  CheckCircle,
  Train,
  Shield,
  Target,
  Clock,
  Hand,
  Gauge,
  AlertTriangle,
  BookOpen,
  Radio,
  Layers,
  Search,
  FileCheck,
  Award,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { PageLayout } from "../components/Layout";
import { SEO, PageSEO } from "../components/SEO";

const TEST_LABELS = {
  "group-bourdon": "Group Bourdon",
  "tea-occ": "TEA-Occ",
  wafv: "WAFV Vigilance",
  trp1: "TRP Part 1",
  trp2: "TRP Part 2",
  atavt: "ATAVT",
  "two-hand": "2HAND",
  mmi: "MMI",
  sje: "SJE",
  "attention-selective": "Selective Attention Practice",
  "attention-divided": "Divided Attention Practice",
  vigilance: "Vigilance Practice",
  memory: "Memory Practice",
  reasoning: "Reasoning Practice",
  perception: "Perception Practice",
  "reaction-time": "Reaction Time Practice",
  "hand-coordination": "Hand Coordination Practice",
  "communication-accuracy": "Communication Accuracy Practice",
  behavioural: "Behavioural Judgement Practice",
};

const SKILLS_DATA = [
  {
    id: "attention",
    name: "Attention",
    icon: Target,
    gradient: "from-blue-500 to-cyan-500",
    assessment: "Group Bourdon and TEA-Occ",
    testSlugs: ["attention-selective", "attention-divided"],
    shortDescription:
      "Selecting the right information, switching attention and managing more than one task safely.",
    definition:
      "RIS-3751-TOM separates attention into selective attention and divided attention. Selective attention is the ability to differentiate between information sources and attend to the correct one. Divided attention is the ability to switch between sources and perform different tasks in parallel.",
    driverMeaning:
      "In the cab, attention is not passive. A driver must monitor lineside signals, in-cab indications, audible warnings, speed, route knowledge, radio messages and operational instructions. The risk is not simply missing something; it is attending to the wrong thing at the wrong time.",
    examples: [
      "Distinguishing a relevant alarm from background cab noise.",
      "Switching between lineside information and in-cab equipment.",
      "Maintaining awareness while making an announcement or dealing with a radio message.",
      "Avoiding fixation on one issue while another risk is developing.",
    ],
    practiceFocus: [
      "Speed with accuracy",
      "Visual scanning discipline",
      "Switching attention without losing the main task",
      "Maintaining performance under repetition",
    ],
    caution:
      "Good attention is not frantic multitasking. It is controlled prioritisation: knowing what matters now, what matters next and what can wait.",
  },
  {
    id: "vigilance",
    name: "Vigilance",
    icon: Eye,
    gradient: "from-emerald-500 to-green-600",
    assessment: "WAFV Vigilance Test",
    testSlugs: ["vigilance"],
    shortDescription:
      "Staying alert to rare but important stimuli over extended periods.",
    definition:
      "Vigilance is the ability to attend and respond to stimuli that occur relatively infrequently over extended periods of time.",
    driverMeaning:
      "Train driving can move between high workload and long periods of routine monitoring. The challenge is that the next safety-critical cue may appear after a long period where very little has changed.",
    examples: [
      "Detecting an infrequent warning after a long quiet period.",
      "Maintaining attention on familiar routes where conditions usually seem normal.",
      "Avoiding underload during monotonous running.",
      "Recognising that low workload can be just as dangerous as overload.",
    ],
    practiceFocus: [
      "Sustained concentration",
      "Low-frequency target detection",
      "Consistency over time",
      "Avoiding performance drop-off",
    ],
    caution:
      "The danger is not always being too busy. In train driving, underload, routine and familiarity can quietly degrade attention.",
  },
  {
    id: "memory",
    name: "Memory",
    icon: Database,
    gradient: "from-pink-500 to-rose-500",
    assessment: "TRP1",
    testSlugs: ["memory"],
    shortDescription:
      "Learning, recalling and applying job-related information under time limits.",
    definition:
      "RIS-3751-TOM defines memory as the ability to learn, recall and apply job-related information in appropriate time limits.",
    driverMeaning:
      "A driver must remember rules, instructions, route features, restrictions, signaller messages, traction procedures and emergency actions. Memory must also be applied correctly, not simply recognised in theory.",
    examples: [
      "Remembering instructions from a signaller.",
      "Learning new rules and procedures during training.",
      "Applying a specific degraded working procedure.",
      "Recalling route knowledge accurately rather than relying on assumption.",
    ],
    practiceFocus: [
      "Short-term recall",
      "Rule retention",
      "Applying remembered information",
      "Avoiding false familiarity",
    ],
    caution:
      "The unsafe pattern is assuming today will be the same as yesterday: this signal always clears, this route is always set, this restriction is never active.",
  },
  {
    id: "reasoning",
    name: "Reasoning",
    icon: Brain,
    gradient: "from-violet-500 to-purple-600",
    assessment: "TRP2",
    testSlugs: ["reasoning"],
    shortDescription:
      "Solving problems, interpreting information and making sound decisions.",
    definition:
      "Reasoning is the ability to solve problems, make decisions, diagnose faults and understand or interpret information from instrumentation.",
    driverMeaning:
      "Reasoning matters when the situation is not just a memory test. Drivers must interpret what information means, identify what has changed and select a safe course of action.",
    examples: [
      "Interpreting fault indications.",
      "Understanding what a developing situation means operationally.",
      "Choosing between available safe options.",
      "Checking whether the action taken has controlled the risk.",
    ],
    practiceFocus: [
      "Information interpretation",
      "Logical problem solving",
      "Rule-based decisions",
      "Checking assumptions",
    ],
    caution:
      "Good reasoning is structured. It avoids guessing, rushing and overconfidence when the information is incomplete.",
  },
  {
    id: "perception",
    name: "Perception",
    icon: Search,
    gradient: "from-amber-500 to-orange-500",
    assessment: "ATAVT",
    testSlugs: ["perception"],
    shortDescription:
      "Anticipating traffic-environment cues and responding correctly to speed and distance.",
    definition:
      "Perception is the ability to anticipate elements in a traffic environment and make a correct decision about how to respond, given speed and distances involved.",
    driverMeaning:
      "Train drivers work ahead of the train. They must anticipate what is coming before it becomes immediate, using route knowledge, landmarks, signal sequences, braking points and environmental cues.",
    examples: [
      "Identifying a landmark before a station and beginning to decelerate.",
      "Anticipating the next signal after a restrictive aspect.",
      "Recognising visual cues quickly enough to act safely.",
      "Judging speed and distance in relation to braking requirements.",
    ],
    practiceFocus: [
      "Rapid visual interpretation",
      "Hazard anticipation",
      "Speed-distance judgement",
      "Observation to action",
    ],
    caution:
      "Perception is not just seeing. It is seeing early enough, understanding correctly and acting in time.",
  },
  {
    id: "reaction-time",
    name: "Reaction Time",
    icon: Zap,
    gradient: "from-yellow-400 to-amber-500",
    assessment: "WAFV and ATAVT",
    testSlugs: ["reaction-time"],
    shortDescription:
      "Responding quickly and adequately to visual and acoustic stimuli.",
    definition:
      "Reaction time covers quick and adequate response to simple and complex visual and acoustic stimuli, alongside the quality of performance.",
    driverMeaning:
      "Railway reaction is not button-mashing. It is recognising the stimulus, understanding its meaning and responding correctly within the available time.",
    examples: [
      "Acknowledging an audible warning correctly.",
      "Responding to a visual indication without losing accuracy.",
      "Maintaining quality of response under time pressure.",
      "Avoiding automatic acknowledgement without conscious understanding.",
    ],
    practiceFocus: [
      "Fast but accurate responses",
      "Visual and auditory stimulus recognition",
      "Response inhibition",
      "Accuracy under pressure",
    ],
    caution:
      "A fast wrong response is still wrong. In safety-critical work, speed and quality have to work together.",
  },
  {
    id: "hand-coordination",
    name: "Hand Coordination",
    icon: Hand,
    gradient: "from-slate-600 to-slate-800",
    assessment: "2HAND",
    testSlugs: ["hand-coordination"],
    shortDescription:
      "Making controlled movements in response to complex stimuli.",
    definition:
      "Hand coordination is the ability to make appropriate and controlled movements in response to decisions about complex stimuli.",
    driverMeaning:
      "Driving involves controlled physical input, not random movement. Power, brake, safety systems and other cab controls require smooth, appropriate and accurate responses.",
    examples: [
      "Coordinating control movements while monitoring indications.",
      "Maintaining precision when workload increases.",
      "Responding physically to complex information.",
      "Avoiding overcorrection or rushed control inputs.",
    ],
    practiceFocus: [
      "Control accuracy",
      "Coordination under pressure",
      "Stimulus-response discipline",
      "Smooth correction",
    ],
    caution:
      "The skill is not simply dexterity. It is controlled movement linked to the correct decision.",
  },
  {
    id: "communication",
    name: "Communication",
    icon: MessageSquare,
    gradient: "from-sky-500 to-blue-600",
    assessment: "MMI",
    testSlugs: ["communication-accuracy"],
    shortDescription:
      "Reading, listening, understanding, responding and conveying information clearly.",
    definition:
      "Communication is the ability to read, listen, understand and respond appropriately, and effectively convey information orally and in writing. The MMI, CBI and DMI assesses oral communication.",
    driverMeaning:
      "Railway communication must be accurate, brief, clear and professional. A driver has to understand instructions, challenge uncertainty, repeat back when required and pass information in a way that reduces risk.",
    examples: [
      "Listening carefully to a signaller’s instruction.",
      "Using structured, professional radio communication.",
      "Confirming understanding rather than assuming.",
      "Explaining safety-relevant information clearly in an interview.",
    ],
    practiceFocus: [
      "Active listening",
      "Clear verbal structure",
      "Relevant examples",
      "Calm explanation under pressure",
    ],
    caution:
      "In train driver selection, communication is not about sounding impressive. It is about being clear, reliable and safe.",
  },
  {
    id: "behavioural-aptitudes",
    name: "Behavioural Aptitudes",
    icon: Shield,
    gradient: "from-red-500 to-rose-600",
    assessment: "SJE and MMI",
    testSlugs: ["behavioural", "communication-accuracy"],
    shortDescription:
      "Safe behavioural tendencies, judgement, responsibility and operational discipline.",
    definition:
      "Behavioural aptitudes relate to whether there are occupational psychological deficiencies, particularly in operational aptitudes or relevant personality factors.",
    driverMeaning:
      "This is where technical ability meets behaviour. The railway needs drivers who can follow rules, manage pressure, admit uncertainty, communicate professionally and make safe decisions even when nobody is watching.",
    examples: [
      "Choosing the safest response in an ambiguous situation.",
      "Reporting a mistake rather than hiding it.",
      "Managing pressure without shortcutting rules.",
      "Showing evidence of responsibility, concentration and self-discipline.",
    ],
    practiceFocus: [
      "Situational judgement",
      "Safety-first decision making",
      "MMI evidence quality",
      "Non-technical skills",
    ],
    caution:
      "The strongest candidates do not present themselves as perfect. They show self-awareness, learning, responsibility and safe judgement.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0 },
};

const SkillCard = ({ skill, isExpanded, onToggle }) => {
  const Icon = skill.icon;

  return (
    <motion.div layout initial="hidden" animate="visible" variants={fadeUp}>
      <Card
        className={`overflow-hidden border-slate-200 bg-white shadow-sm transition-all ${
          isExpanded ? "ring-2 ring-[#0B1D51] shadow-lg" : "hover:shadow-md"
        }`}
      >
        <CardContent className="p-0">
          <button
            onClick={onToggle}
            className="w-full p-5 text-left transition-colors hover:bg-slate-50 sm:p-6"
            aria-expanded={isExpanded}
          >
            <div className="flex items-start gap-4">
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${skill.gradient} shadow-sm`}
              >
                <Icon className="h-7 w-7 text-white" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-xl font-bold text-[#0B1D51] sm:text-2xl">
                    {skill.name}
                  </h3>
                  <Badge variant="outline" className="border-slate-300 text-slate-600">
                    {skill.assessment}
                  </Badge>
                </div>
                <p className="max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  {skill.shortDescription}
                </p>
              </div>

              <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              </div>
            </div>
          </button>

          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden border-t border-slate-100"
              >
                <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-3">
                  <div className="lg:col-span-2">
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#0B1D51]">
                        <BookOpen className="h-4 w-4 text-[#FF9F1C]" />
                        What this means
                      </div>
                      <p className="text-sm leading-relaxed text-slate-700">
                        {skill.definition}
                      </p>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#0B1D51]">
                        <Train className="h-4 w-4 text-[#FF9F1C]" />
                        Why it matters in the cab
                      </div>
                      <p className="text-sm leading-relaxed text-slate-700">
                        {skill.driverMeaning}
                      </p>
                    </div>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 p-4">
                        <h4 className="mb-3 flex items-center gap-2 font-semibold text-[#0B1D51]">
                          <Gauge className="h-4 w-4 text-[#2CB67D]" />
                          Real-world examples
                        </h4>
                        <ul className="space-y-2">
                          {skill.examples.map((item) => (
                            <li key={item} className="flex gap-2 text-sm text-slate-700">
                              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#2CB67D]" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4">
                        <h4 className="mb-3 flex items-center gap-2 font-semibold text-[#0B1D51]">
                          <FileCheck className="h-4 w-4 text-[#2CB67D]" />
                          Practice focus
                        </h4>
                        <ul className="space-y-2">
                          {skill.practiceFocus.map((item) => (
                            <li key={item} className="flex gap-2 text-sm text-slate-700">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF9F1C]" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">
                      <div className="mb-1 flex items-center gap-2 font-semibold">
                        <AlertTriangle className="h-4 w-4" />
                        Candidate warning
                      </div>
                      {skill.caution}
                    </div>
                  </div>

                  <aside className="space-y-4">
                    <div className="rounded-2xl bg-[#0B1D51] p-4 text-white">
                      <div className="mb-2 flex items-center gap-2 font-semibold">
                        <Award className="h-4 w-4 text-[#FF9F1C]" />
                        RIS-linked assessment
                      </div>
                      <p className="text-sm text-white/80">{skill.assessment}</p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 p-4">
                      <h4 className="mb-3 font-semibold text-[#0B1D51]">
                        Practise this area
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {skill.testSlugs.map((slug) => (
                          <Link
                            key={slug}
                            to={`/tests/${slug}`}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-[#0B1D51] hover:text-white"
                          >
                            {TEST_LABELS[slug] || slug}
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                        ))}
                      </div>
                    </div>

                    {skill.id === "communication" && (
                      <Link
                        to="https://www.traindriverfoundation.com/interview"
                        className="group block overflow-hidden rounded-2xl border border-[#FF9F1C]/40 bg-gradient-to-br from-[#0B1D51] via-[#12306F] to-[#0B1D51] p-4 text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FF9F1C] text-white shadow-sm">
                            <MessageSquare className="h-6 w-6" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <Badge className="bg-white/15 text-white hover:bg-white/15">
                                AI interview practice
                              </Badge>
                            </div>

                            <h4 className="font-heading text-lg font-bold leading-tight">
                              Try TDF&apos;s Interview Simulator
                            </h4>

                            <p className="mt-2 text-sm leading-relaxed text-white/80">
                              Practise structured interviews, CBI, MMI and DMI-style examples.
                              Directly simulates interviews. Different coaching modes available.
                            </p>

                            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#FF9F1C]">
                              Open Interview Simulator
                              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    )}
                  </aside>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const SkillsPage = () => {
  const [expandedSkill, setExpandedSkill] = useState("attention");

  const selectedSkill = useMemo(
    () => SKILLS_DATA.find((skill) => skill.id === expandedSkill),
    [expandedSkill]
  );

  const openSkill = (skillId) => {
    setExpandedSkill(skillId);

    setTimeout(() => {
      document
        .getElementById(`skill-${skillId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  return (
    <PageLayout>
      <SEO {...PageSEO.skills} />

      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white">
        <section className="relative overflow-hidden bg-[#0B1D51] text-white">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#FF9F1C] blur-3xl" />
            <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-blue-400 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <motion.div initial="hidden" animate="visible" variants={fadeUp}>
                <Badge className="mb-5 bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]">
                  RIS-3751-TOM Issue 4 aligned
                </Badge>

                <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                  Train driver skills are measurable.
                  <span className="block text-[#FF9F1C]">
                    That is why they are tested.
                  </span>
                </h1>

                <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/82 sm:text-xl">
                  UK train driver psychometric selection is built around core
                  cognitive, psychomotor and behavioural aptitudes. This page explains
                  what each skill means in plain English, why it matters in the cab, and
                  which practice areas help candidates prepare.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link to="/tests">
                    <Button size="lg" className="bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]/90">
                      Start practising
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>

                  <Link to="/pricing">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-white/60 bg-transparent text-white hover:bg-white/10"
                    >
                      View pricing
                    </Button>
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: 0.1 }}
                className="rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FF9F1C]">
                    <Shield className="h-6 w-6 text-white" />
                  </div>

                  <div>
                    <p className="font-semibold">Assessment areas</p>
                    <p className="text-sm text-white/70">
                      Standardised selection criteria
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {SKILLS_DATA.slice(0, 6).map((skill) => {
                    const Icon = skill.icon;

                    return (
                      <div
                        key={skill.id}
                        className="rounded-2xl border border-white/10 bg-white/10 p-3"
                      >
                        <Icon className="mb-2 h-5 w-5 text-[#FF9F1C]" />
                        <p className="text-sm font-semibold">{skill.name}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 rounded-2xl bg-white p-4 text-[#0B1D51]">
                  <p className="text-sm font-semibold">
                    Also assessed: communication and behavioural aptitudes through
                    MMI, CBI, DMI and SJE.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="mb-3">
                Skill map
              </Badge>

              <h2 className="font-heading text-3xl font-bold text-[#0B1D51] sm:text-4xl">
                Explore the core train driver aptitude areas
              </h2>

              <p className="mt-3 text-slate-600">
                Select any area below to open the full explanation and related
                practice links.
              </p>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {SKILLS_DATA.map((skill) => {
                const Icon = skill.icon;
                const active = expandedSkill === skill.id;

                return (
                  <button
                    key={skill.id}
                    onClick={() => openSkill(skill.id)}
                    className={`group rounded-2xl border p-4 text-left transition-all ${
                      active
                        ? "border-[#0B1D51] bg-[#0B1D51] text-white shadow-lg"
                        : "border-slate-200 bg-white hover:border-[#0B1D51]/40 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                          active ? "bg-white/15" : "bg-slate-100"
                        }`}
                      >
                        <Icon
                          className={`h-5 w-5 ${
                            active ? "text-[#FF9F1C]" : "text-[#0B1D51]"
                          }`}
                        />
                      </div>

                      <div>
                        <p className="font-bold">{skill.name}</p>
                        <p
                          className={`mt-1 text-sm ${
                            active ? "text-white/75" : "text-slate-600"
                          }`}
                        >
                          {skill.assessment}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
            <div className="mb-7 grid gap-5 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
              <div>
                <Badge className="mb-3 bg-[#0B1D51] text-white">
                  Current focus
                </Badge>

                <h2 className="font-heading text-3xl font-bold text-[#0B1D51] sm:text-4xl">
                  {selectedSkill ? selectedSkill.name : "Skill breakdown"}
                </h2>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex gap-3">
                  <Radio className="mt-1 h-5 w-5 shrink-0 text-[#FF9F1C]" />
                  <p className="text-sm leading-relaxed text-slate-700">
                    TDF practice does not guarantee a pass. It is designed to build
                    familiarity with the types of cognitive effort, time pressure,
                    accuracy demands and behavioural judgement relevant to train
                    driver selection.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {SKILLS_DATA.map((skill) => (
                <div key={skill.id} id={`skill-${skill.id}`}>
                  <SkillCard
                    skill={skill}
                    isExpanded={expandedSkill === skill.id}
                    onToggle={() =>
                      setExpandedSkill(expandedSkill === skill.id ? null : skill.id)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
            <div className="grid gap-5 md:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <Clock className="mb-4 h-8 w-8 text-[#FF9F1C]" />
                <h3 className="font-heading text-xl font-bold text-[#0B1D51]">
                  Time pressure
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Many assessment tasks require candidates to work quickly while
                  protecting accuracy. Practice should build controlled pace, not
                  careless speed.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <Layers className="mb-4 h-8 w-8 text-[#FF9F1C]" />
                <h3 className="font-heading text-xl font-bold text-[#0B1D51]">
                  Repetition
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Real performance depends on consistency. A good candidate needs to
                  maintain standards after the first minute, not only at the start.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <Lightbulb className="mb-4 h-8 w-8 text-[#FF9F1C]" />
                <h3 className="font-heading text-xl font-bold text-[#0B1D51]">
                  Understanding
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Practice is most useful when candidates understand what a test is
                  measuring and how that skill connects to safe train driving.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#0B1D51]">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center sm:px-6 sm:py-18">
            <Train className="mx-auto mb-5 h-12 w-12 text-[#FF9F1C]" />

            <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">
              Build the skills behind the assessment
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-white/80">
              Practise attention, vigilance, memory, reasoning, perception, reaction
              time, coordination and judgement with rail-focused preparation tools.
            </p>

            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/tests">
                <Button size="lg" className="bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]/90">
                  Explore practice tests
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>

              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/60 bg-transparent text-white hover:bg-white/10"
                >
                  See pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
};

export default SkillsPage;